<template>
  <div>
    <v-dialog
      :value="value && showSelectDialog"
      persistent
      @input="$emit('input')"
      :content-class="'create_new_account_modal'"
      max-width="700"
    >
      <div class="heading">
        <h2 class="heading_new_account">
          {{ trackNumber ? `Select Artists for Track ${trackNumber}` : 'Select Secondary Artists' }}
        </h2>
      </div>
      
      <div class="search-bar-container">
        <div class="search-input-wrapper">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Find or create an artist..."
            :class="{'error_name': filteredArtists.length === 0 && searchQuery.length > 0}"
            @input="searchArtists"
          />
          <button 
            v-if="searchQuery.length > 0" 
            type="button" 
            class="clear_search" 
            @click="clearSearch"
          ></button>
        </div>
      </div>
      
      <!-- Artist Search Step -->
      <div class="wrapper_content">
        <!-- Album Artists Section (always visible) -->
        <div class="album-artists-section" v-if="albumArtists && albumArtists.length > 0">
          <div class="section-heading">
            {{ albumArtists.length > 1 ? 'Album Artists (automatically included)' : 'Album Artist (automatically included)' }}
          </div>
          <div class="album-artists-list">
            <!-- Album Artists (Main and Secondary) -->
            <div 
              v-for="artist in albumArtists" 
              :key="artist.id" 
              class="item_account album-artist"
              :class="{
                'main-artist': artist.isMainArtist, 
                'secondary-artist': artist.isSecondaryArtist
              }"
            >
              <div class="data">
                <div class="img">
                  <img 
                    v-if="artist.avatar && artist.avatar.contentUrl" 
                    :src="getAvatarUrl(artist.avatar)"
                    :alt="artist.name">
                  <div class="default_user_avatar" v-else></div>
                </div>
                <div class="name_account">
                  <div class="name">
                    {{ artist.name }}
                    <span v-if="artist.isMainArtist" class="artist-role">(Main Artist)</span>
                    <span v-else-if="artist.isSecondaryArtist" class="artist-role">(Secondary Artist)</span>
                  </div>
                </div>
              </div>
              <div class="action">
                <div class="check_round checked"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="create_account_box fixed-height">
          <div v-if="isSearching" class="list-loading-container">
            <v-progress-circular :size="40" :width="3" color="#20C4F5" indeterminate></v-progress-circular>
            <span>Searching for artists...</span>
          </div>
          
          <div v-else-if="!isSearching && filteredArtists.length === 0 && !searchQuery" class="list-loading-container">
            <div class="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#7481A6"/>
              </svg>
            </div>
            <span>Select additional artists to feature on this track.</span>
          </div>
          
          <div v-else-if="!isSearching && filteredArtists.length === 0 && searchQuery.length > 0" class="list-loading-container">
            <div class="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#7481A6"/>
              </svg>
            </div>
            <span>No matching Artist accounts found.</span>
            
            <!-- Create button -->
            <button 
              type="button" 
              class="c2a_btn sign_up fizz-effect create-btn" 
              v-ripple 
              @click="createNewArtist"
              ref="createButton"
            >
              Create Artist
              <div 
                v-for="n in 8" 
                :key="n" 
                class="bubble-stream"
                :style="getBubbleStyle(n)"
              ></div>
            </button>
          </div>
          
          <div class="list_accounts" v-else-if="filteredArtists.length > 0">
            <div class="section-heading track-artists-heading">Additional Track Artists</div>
            <div 
              v-for="artist in filteredArtists" 
              :key="artist.id" 
              class="item_account"
              :class="{ 
                'selected': isArtistSelected(artist),
                'disabled': isAlbumArtist(artist)
              }"
              @click="toggleArtistSelection(artist)"
            >
              <div class="data">
                <div class="img">
                  <img 
                    v-if="artist.avatar && artist.avatar.contentUrl" 
                    :src="getAvatarUrl(artist.avatar)"
                    :alt="artist.name">
                  <div class="default_user_avatar" v-else></div>
                </div>
                <div class="description">
                  <div class="name">{{ artist.name }}</div>
                  <div class="type" v-if="artist.hasActiveSubscription">Artist</div>
                  <div v-else class="type restricted">
                    Inactive subscription
                  </div>
                </div>
              </div>
              <div class="action">
                <div class="check_box" :class="{'checked': isArtistSelected(artist)}"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="wrapper_button center">
        <button type="button" class="tetriatary_btn" v-ripple @click="cancel">Cancel</button>
        
        <!-- Standard Select button -->
        <button 
          type="button" 
          class="tetriatary_btn" 
          v-ripple 
          @click="confirmSelection"
          :class="{ 'loading': loading }"
        >
          <v-progress-circular v-if="loading" :width="2" indeterminate></v-progress-circular>
          Apply
        </button>
      </div>
    </v-dialog>
    
    <!-- Subscription issue dialog -->
    <v-dialog 
      v-if="accountIssueDialog"
      v-model="accountIssueDialog"
      persistent
      :content-class="'create_new_account_modal'"
      max-width="700"
    >
      <div class="heading">
        <h2 class="heading_new_account">
          Subscription Issue
        </h2>
      </div>
      <div class="wrapper_content h165 v_center">
        <div class="account_issue_box">
          <p>This artist account doesn't have an active subscription.</p>
          <p>Please renew the subscription or choose another artist.</p>
        </div>
      </div>
      <div class="wrapper_button center">
        <button type="button" class="tetriatary_btn" v-ripple @click="accountIssueDialog = false">Back</button>
      </div>
    </v-dialog>

    <!-- New Account Dialog -->
    <new-account
      v-model="showNewAccountDialog"
      :initial-name="searchQuery"
      :uses="'accounts'"
      ref="newAccountComponent"
      @account-created="onAccountCreated"
      @input="onNewAccountDialogChange"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NewAccount from "./newAccount.vue";

export default {
  name: "TrackArtistSelector",
  components: {
    NewAccount
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    trackNumber: {
      type: [Number, String],
      default: ''
    },
    // Selected track artists passed from parent
    selectedArtists: {
      type: Array,
      default() {
        return [];
      }
    },
    // Album artists passed from parent (from Step 1)
    albumArtists: {
      type: Array,
      default() {
        return [];
      }
    },
  },
  data() {
    return {
      showSelectDialog: true,
      showNewAccountDialog: false,
      accountIssueDialog: false,
      loading: false,
      isSearching: false,
      searchQuery: '',
      filteredArtists: [],
      selectedTrackArtists: [],
      allArtists: [],
      bubbleStyles: {}
    };
  },
  computed: {
    ...mapGetters([
      'YOUR_ARTISTS',
      'userID',
      'SEARCH_ARTISTS_RESULT'
    ]),
    
    // Combine album artists and selected track artists for the final selection
    allSelectedArtists() {
      // Create a new array with all selected artists
      const albumArtistIds = this.albumArtists.map(artist => artist.id);
      
      // Filter out any duplicates (artists that are both album artists and selected track artists)
      const trackOnlyArtists = this.selectedTrackArtists.filter(artist => 
        !albumArtistIds.includes(artist.id)
      );
      
      return [...this.albumArtists, ...trackOnlyArtists];
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // When opening, reset and load artists
        this.searchQuery = '';
        this.loadInitialArtists();
      }
    }
  },
  created() {
    // Initialize selected artists from prop
    this.selectedTrackArtists = this.selectedArtists ? [...this.selectedArtists] : [];
    this.loadInitialArtists();
  },
  methods: {
    ...mapActions(['GET_YOUR_ARTISTS', 'SEARCH_ARTISTS']),
    
    isAlbumArtist(artist) {
      return this.albumArtists.some(albumArtist => albumArtist.id === artist.id);
    },
    
    isArtistSelected(artist) {
      return this.selectedTrackArtists.some(selectedArtist => selectedArtist.id === artist.id) ||
             this.isAlbumArtist(artist);
    },
    
    toggleArtistSelection(artist) {
      // Don't allow toggling album artists
      if (this.isAlbumArtist(artist)) {
        return;
      }
      
      // Check if artist has active subscription
      if (!artist.hasActiveSubscription) {
        this.accountIssueDialog = true;
        return;
      }
      
      const index = this.selectedTrackArtists.findIndex(a => a.id === artist.id);
      
      if (index === -1) {
        // Artist not selected, add them
        this.selectedTrackArtists.push({
          id: artist.id,
          name: artist.name,
          avatar: artist.avatar || null
        });
      } else {
        // Artist already selected, remove them
        this.selectedTrackArtists.splice(index, 1);
      }
    },
    
    getBubbleStyle(index) {
      if (!this.bubbleStyles[index]) {
        // Random parameters for each bubble
        const leftPos = 5 + Math.floor(Math.random() * 85); // 5-90%
        const animDuration = 1.5 + (Math.random() * 1.5); // 1.5-3s
        const animDelay = -(Math.random() * 2); // -0 to -2s
        
        this.bubbleStyles[index] = {
          left: `${leftPos}%`,
          animation: `floatUp ${animDuration}s infinite linear`,
          animationDelay: `${animDelay}s`
        };
      }
      
      return this.bubbleStyles[index];
    },
    
    resetBubbleStyles() {
      this.bubbleStyles = {};
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.filteredArtists = this.YOUR_ARTISTS;
      this.resetBubbleStyles();
    },
    
    createNewArtist() {
      this.showNewAccountDialog = true;
      this.showSelectDialog = false;
      
      // Use nextTick to ensure the component is mounted before accessing it
      this.$nextTick(() => {
        if (this.$refs.newAccountComponent) {
          // Set artist account type directly
          this.$refs.newAccountComponent.selectedTypeAccount = 'Artist';
          // Skip to subscription selection step
          this.$refs.newAccountComponent.changeStep(2);
          
          // Override the cancel method
          const originalCancel = this.$refs.newAccountComponent.cancel;
          this.$refs.newAccountComponent.cancel = () => {
            this.showNewAccountDialog = false;
            this.showSelectDialog = true;
          };
          
          // Override the back button behavior in step 2
          const originalChangeStep = this.$refs.newAccountComponent.changeStep;
          this.$refs.newAccountComponent.changeStep = (step) => {
            // If trying to go to step 1 (account type selection) from step 2 (subscription)
            if (step === 1 && this.$refs.newAccountComponent.step === 2) {
              // Show the track artist selector instead
              this.showNewAccountDialog = false;
              this.showSelectDialog = true;
            } else {
              // Otherwise use the original method
              originalChangeStep.call(this.$refs.newAccountComponent, step);
            }
          };
        }
      });
    },
    
    loadInitialArtists() {
      this.isSearching = true;
      this.GET_YOUR_ARTISTS({
        owner: this.userID,
        deletedAt: false, 
        'order[createdAt]': 'desc'
      })
        .then(() => {
          this.filteredArtists = this.YOUR_ARTISTS;
        })
        .catch(err => {
          console.error('Error loading artists:', err);
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    
    async searchArtists() {
      if (!this.searchQuery) {
        this.filteredArtists = this.YOUR_ARTISTS;
        return;
      }

      this.isSearching = true;
      this.resetBubbleStyles(); // Reset bubble styles before new search
      
      try {
        await this.SEARCH_ARTISTS({ name: this.searchQuery });
        this.filteredArtists = this.SEARCH_ARTISTS_RESULT;
      } catch (err) {
        console.error('Error searching artists:', err);
        // Fallback to filtering user's artists
        const query = this.searchQuery.toLowerCase();
        this.filteredArtists = this.YOUR_ARTISTS.filter(artist => 
          artist.name.toLowerCase().includes(query)
        );
      } finally {
        this.isSearching = false;
      }
    },
    
    confirmSelection() {
      this.loading = true;
      
      // Emit the selected artists back to the parent component
      setTimeout(() => {
        this.loading = false;
        this.$emit('selection-confirmed', this.selectedTrackArtists);
        this.$emit('input', false); // Close the dialog
      }, 300);
    },
    
    cancel() {
      // Close the dialog without saving changes
      this.$emit('input', false);
    },
    
    getAvatarUrl(avatar) {
      if (!avatar) return null;
      
      // First try to use thumbnail if available
      if (avatar.thumbnail && avatar.thumbnail['50x50']) {
        return `${avatar.thumbnail['50x50']}?cache=${new Date().getTime()}`;
      }
      
      // Otherwise use content URL
      const contentUrl = avatar.contentUrl;
      if (!contentUrl) return null;
      
      const fullUrl = contentUrl.startsWith('/') 
        ? `${this.apiUrl}${contentUrl.slice(1)}` 
        : `${this.apiUrl}${contentUrl}`;
        
      return `${fullUrl}?cache=${new Date().getTime()}`;
    },
    
    onAccountCreated(newArtist) {
      this.showNewAccountDialog = false;
      
      // Add the new artist to the selected artists
      this.selectedArtists.push({
        id: newArtist.id,
        name: newArtist.name
      });
      
      // Also update the filtered artists list
      this.filteredArtists.unshift(newArtist);
      
      this.showSelectDialog = true;
    },
    
    onNewAccountDialogChange(value) {
      this.showNewAccountDialog = value;
      if (!value) {
        // When new account dialog is closed, show the select dialog again
        this.showSelectDialog = true;
      }
    }
  },
  created() {
    console.log(`TrackArtistSelector created for track index: ${this.trackIndex}, title: ${this.trackTitle}`);
    this.loadInitialArtists();
  }
}
</script>

<style lang="scss">
/* Component-specific styles moved to shared stylesheet at: 
   src/assets/styles/components/newAccount.scss */

.search-container {
  width: 100%;
  padding: 10px 0;
}

.search-bar-container {
  padding: 20px 20px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-heading {
  font-size: 14px;
  font-weight: 500;
  color: #7481A6;
  padding: 10px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.track-artists-heading {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.wrapper_content {
  display: flex;
  gap: 20px;
  padding: 20px;
}

.album-artists-section,
.create_account_box {
  flex: 1;
  min-width: 0; /* Prevent flex items from overflowing */
}

.album-artists-section {
  background-color: rgba(32, 196, 245, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(32, 196, 245, 0.2);
  padding: 15px;
  height: fit-content;

  .section-heading {
    font-weight: 600;
    margin-bottom: 10px;
    color: #20C4F5;
  }

  .album-artists-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .main-artist {
    border-left: 3px solid #20C4F5;
    background-color: rgba(32, 196, 245, 0.1);
    
    .type {
      color: #20C4F5;
    }
  }
  
  .secondary-artist {
    border-left: 3px solid #9c27b0;
    background-color: rgba(156, 39, 176, 0.05);
  }
  
  .artist-role {
    font-size: 0.8em;
    opacity: 0.7;
    margin-left: 5px;
    font-style: italic;
  }
}

.fixed-height {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #282C47;
  border-radius: 5px;
  padding: 0;
}

.item_account {
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.check_box {
  width: 18px;
  height: 18px;
  border: 1px solid #7481A6;
  border-radius: 3px;
  position: relative;
  
  &.checked {
    background-color: #20C4F5;
    border-color: #20C4F5;
    
    &:after {
      content: '';
      position: absolute;
      width: 6px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      top: 2px;
      left: 6px;
      transform: rotate(45deg);
    }
  }
}

.search-input-wrapper {
  position: relative;
  width: 100%;
  
  &:after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M10 18C11.775 17.9996 13.4988 17.4054 14.897 16.312L19.293 20.708L20.707 19.294L16.311 14.898C17.405 13.4997 17.9996 11.7754 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10C2 14.411 5.589 18 10 18ZM10 4C13.309 4 16 6.691 16 10C16 13.309 13.309 16 10 16C6.691 16 4 13.309 4 10C4 6.691 6.691 4 10 4Z' fill='%23dcdcdc'/%3e%3cpath d='M11.4121 8.58609C11.7911 8.96609 12.0001 9.46809 12.0001 10.0001H14.0001C14.001 9.47451 13.8977 8.95398 13.6961 8.46857C13.4946 7.98316 13.1989 7.54251 12.8261 7.17209C11.3121 5.66009 8.68707 5.66009 7.17407 7.17209L8.58607 8.58809C9.34607 7.83009 10.6561 7.83209 11.4121 8.58609Z' fill='%23dcdcdc'/%3e%3c/svg%3e");
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    opacity: 0.8;
    z-index: 1;
  }
  
  input {
    width: 100%;
    height: 52px;
    background: #0C1324;
    border: 1px solid #282C47;
    border-radius: 5px;
    overflow: hidden;
    color: #fff;
    font-size: 16px;
    padding: 0 45px 0 40px;
    transition: color 0.2s ease, border-color 0.2s ease;
    
    &.error_name {
      border: 1px solid #E94E4E;
    }
    
    &::placeholder {
      color: #7481A6;
      opacity: 0.7;
      transition: opacity 0.2s ease;
    }
    
    &:focus {
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.5);
      &::placeholder {
        opacity: 0.5;
      }
    }
  }
  
  .clear_search {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 15px;
    width: 15px;
    height: 15px;
    background-position: center;
    background-size: contain;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='18' height='19' viewBox='0 0 18 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M1.54722 17.9619L16.6417 1.97953M16.6417 17.9619L1.54722 1.97953' stroke='%23dcdcdc' stroke-width='2' stroke-linecap='round'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    opacity: 0.8;
    transition: opacity 0.2s ease;
    background-color: transparent;
    border: none;
    cursor: pointer;
    
    &::before {
      content: '';
      position: absolute;
      top: -15px;
      right: -15px;
      bottom: -15px;
      left: -15px;
    }
    
    &:hover {
      opacity: 1;
    }
  }
}

.list-loading-container {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  color: #7481A6;
  gap: 10px;
  text-align: center;
  background: #0C1324;
  border: 1px solid #282C47;
  border-radius: 5px;
  margin-bottom: 15px;
}

.list_accounts {
  max-height: 330px;
  overflow-y: auto;
  border: 1px solid #282C47;
  border-radius: 5px;
  background-color: #0C1324;
  margin-bottom: 15px;
}

.empty-icon {
  opacity: 0.6;
}

.c2a_btn {
  width: auto;
  min-width: 120px;
  height: 55px;
  border-radius: 50px;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &.sign_up {
    background: rgba(0, 200, 100, 0.9) !important;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    
    &:hover {
      background: rgba(0, 210, 110, 0.95) !important;
    }
  }
  
  &.create-btn {
    margin-top: 20px;
    height: 45px;
    font-size: 15px;
    max-width: 200px;
  }
}

.fizz-effect {
  position: relative;
  overflow: hidden;

  // Bubbles container
  &:after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 200px;
    bottom: 100%;
    pointer-events: none;
  }

  // Multiple bubble streams
  .bubble-stream {
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    opacity: 0.6;
    bottom: 0;
    /* Individual bubble styles now applied inline */
  }
}

@keyframes floatUp {
  0% {
    transform: translateY(100px) scale(0.5);
  }
  100% {
    transform: translateY(-100px) scale(1.3);
  }
}
</style> 